import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { jwtDecode } from "jwt-decode";
import { useAccessToken } from "../../auth/useAccessToken";
import { createSupportTicket } from "../../services/UserRole/SupportTicketService";
import SectionSpinner from "../../CommonComponents/common/Loader/SectionSpinner";
import { APPLICATION_LIST } from "../../constants/userAccess";
import { useLocation } from "react-router-dom";

const MENU_OPTIONS = {
  1: {
    label: "New User Request Access",
    subMenus: ["Approval Workflow Creation", "NDA Form"],
  },
  2: {
    label: "Access Request Status",
    subMenus: ["Delay", "Change in Approver"],
  },
  3: {
    label: "Report Screen",
    subMenus: ["Details Missing", "Remove User"],
  },
  4: {
    label: "Approval Delegation",
    subMenus: ["User Not Found", "Delegation Not Working"],
  },
};

const DEFAULT_CATEGORY_OPTIONS = [
  "Data Missing",
  "Access Issue",
  "Other Issue",
];

const ROLE_CATEGORY_OPTIONS = [
  "Role not Available in Form",
  "Need to change Role",
  "Others",
];

const SupportTicketDialog = ({ open, onClose, selectedApplication }) => {
  const [application, setApplication] = useState("");
  const [menu, setMenu] = useState("");
  const [subMenu, setSubMenu] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [loggedInEmail, setLoggedInEmail] = useState("");
  const accessToken = useAccessToken();
  const location = useLocation();
  const hideApplicationField = location.pathname.includes("/approval-rights") || location.pathname.includes("/bottler-admin") ||
    location.pathname.includes("/access-control");

  useEffect(() => {
    if (open) {
      if (hideApplicationField) {
        setApplication(selectedApplication || "Approver Configuration");
      } else {
        setApplication(selectedApplication || "");
      }
    }
  }, [open, selectedApplication, hideApplicationField]);

  useEffect(() => {
    if (!accessToken) return;
    try {
      const parsed = jwtDecode(accessToken);
      setLoggedInEmail(parsed?.unique_name || "");
    } catch (err) {
      console.error("Failed to decode token:", err);
    }
  }, [accessToken]);

  const handleMenuChange = (e) => {
    setMenu(e.target.value);
    setSubMenu("");
    setCategory("");
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const payload = {
      applicationType: application,
      userEmail: loggedInEmail,
      menu: MENU_OPTIONS[menu]?.label,
      subMenu,
      category,
      urgency: priority,
      description: `${application}: ${description}`,
      assignedTo: "IT: Application - Autonomous Layer",
      "functionality": "Autonomous Layer"
    };

    try {
      const response = await createSupportTicket(payload);

      if (response?.statusCode === 201) {
        setSnackbar({
          open: true,
          message: (
            <>
              {response.message} — <strong>Ticket No:</strong>{" "}
              {response.ticketNo}
            </>
          ),
          severity: "success",
        });
        onClose();
      } else {
        throw new Error(response?.message || "Ticket creation failed");
      }
    } catch (error) {
      console.error("Ticket creation failed:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to create support ticket",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {submitting && <SectionSpinner source="SupportTicketDialog" />}
      <Dialog
        open={open}
        onClose={submitting ? undefined : onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: submitting
            ? {
              filter: "blur(2px)",
              pointerEvents: "none",
              transition: "filter 0.2s ease",
            }
            : {},
        }}
        BackdropProps={{
          sx: submitting
            ? {
              backdropFilter: "blur(2px)",
            }
            : {},
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            fontWeight: 700,
          }}
        >
          Support Ticket
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            {!hideApplicationField && (
              <TextField
                select
                label="Application"
                value={application}
                onChange={(e) => {
                  setApplication(e.target.value);
                  setMenu("");
                  setSubMenu("");
                  setCategory("");
                }}
                fullWidth
              >
                {APPLICATION_LIST.map((app) => (
                  <MenuItem key={app} value={app}>
                    {app}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              select
              label="Menu"
              value={menu}
              onChange={handleMenuChange}
              fullWidth
              disabled={!application}
            >
              {Object.entries(MENU_OPTIONS).map(([key, item]) => (
                <MenuItem key={key} value={key}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Sub Menu"
              value={subMenu}
              onChange={(e) => {
                setSubMenu(e.target.value);
                setCategory("");
              }}
              fullWidth
              disabled={!menu}
            >
              {menu &&
                MENU_OPTIONS[menu].subMenus.map((sub) => {
                  let updatedSubMenu = sub;

                  if (sub === "NDA Form") {
                    updatedSubMenu =
                      application === "Command Center"
                        ? "NDA Form"
                        : "Role Based";
                  }

                  return (
                    <MenuItem key={updatedSubMenu} value={updatedSubMenu}>
                      {updatedSubMenu}
                    </MenuItem>
                  );
                })}
            </TextField>

            <TextField
              select
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              fullWidth
              disabled={!subMenu}
            >
              {(subMenu === "Role Based"
                ? ROLE_CATEGORY_OPTIONS
                : DEFAULT_CATEGORY_OPTIONS
              ).map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              fullWidth
            >
              <MenuItem value="Standard">Standard</MenuItem>
              <MenuItem value="Urgent">Urgent</MenuItem>
            </TextField>

            <TextField
              select
              label="Assigned To"
              value="AL Team"
              fullWidth
              disabled
            >
              <MenuItem value="AL Team">AL Team</MenuItem>
            </TextField>
          </Stack>

          <Stack spacing={2}>
            <TextField
              label="Issue Description"
              multiline
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
            />

            <TextField
              label="Initiator"
              value={loggedInEmail || ""}
              disabled
              fullWidth
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="inherit" disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#4b4b4bff",
              color: "#fff",
              height: 30,
              "&:hover": { backgroundColor: "#2d2d2dff", color: "#fff" },
              "&.Mui-disabled": {
                color: "#fff",
                opacity: 0.7,
              },
            }}
            onClick={handleSubmit}
            disabled={submitting}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SupportTicketDialog;